import clsx from 'clsx';
import Style from './AppAttachmentsV2.module.scss';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppButton } from 'uikit/buttons';
import { TypographyStyles } from 'styles';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { ReactComponent as FileIcon } from 'assets/img/generalBuilder/file_16.svg';
import { ReactComponent as BigFileIcon } from 'assets/img/generalBuilder/file_64.svg';
import { ReactComponent as CrossIcon } from 'assets/img/generalBuilder/cross_16.svg';
import { formatFileSize } from 'src/helpers/file-helper';
import { AppAttachmentsProps, AppAttachmentValue, AppAttachmentValueWithErrors } from './AppAttachmentsV2.d';
import { first, isNil } from 'lodash';

export const AppAttachmentsV2: FC<AppAttachmentsProps> = ({
  value,
  addFileButtonText,
  fileTypes,
  disabled,
  multiple,
  hideInput,
  onChange,
  readOnly,
  maxFileSizeMb,
}) => {
  const { t } = useScopedTranslation('uikit.Attachments');
  const ref = useRef<HTMLInputElement | null>(null);
  const { ref: dashedRef, isDraggedOver } = useDragOver();

  const addFiles = useCallback(() => {
    if (ref && ref.current) ref.current.click();
  }, [ref]);

  const clearInputValue = useCallback((event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const element = event.target as HTMLInputElement;
    element.value = '';
  }, []);

  const deleteFile = useCallback(
    (file: AppAttachmentValue) => {
      if (!value) return;
      onChange?.(value.filter((f) => f !== file));
    },
    [onChange, value],
  );

  const addFilesHandler = useCallback(
    (files: FileList | null) => {
      if (!files || !files.length || readOnly || disabled) return;

      if (multiple) {
        const inputFilesArr = Array.from(files);
        const difference: File[] = inputFilesArr.filter((x) => !value?.map((m) => m.fileName).includes(x.name));
        onChange?.([...(value ?? []), ...difference.map(fileToAppAttachmentValue)]);
        return;
      } else {
        const firstFile = first(files);
        onChange?.([fileToAppAttachmentValue(firstFile!)]);
      }
    },
    [disabled, multiple, onChange, readOnly, value],
  );

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      addFilesHandler(e.target.files);
    },
    [addFilesHandler],
  );

  const handleDrop = (event: React.DragEvent<HTMLInputElement>) => {
    event.preventDefault();
    addFilesHandler(event.dataTransfer.files);
  };

  return (
    <div className={clsx(Style.container, { [Style.disabled]: disabled })}>
      {!hideInput && (
        <>
          {!!fileTypes?.length && (
            <div className={Style.extensions}>
              {t('SupportedTypes', {
                formats: fileTypes.map((x) => `*${x}`).join(', '),
              })}
            </div>
          )}
          <div
            ref={dashedRef}
            className={clsx(Style.dashes, {
              [Style.dragOver]: isDraggedOver && !readOnly && !disabled,
            })}
            onDrop={handleDrop}
            onDragOver={(event) => event.preventDefault()}
          >
            <BigFileIcon className={Style.icon} />
            <AppButton
              disabled={disabled}
              className={clsx(TypographyStyles.paragraph14)}
              onClick={addFiles}
              variant={'icon-link'}
              colorSchema={'primary'}
              text={addFileButtonText}
              readOnly={readOnly}
            />
            {maxFileSizeMb && <div className={Style.maxFileSize}>{t('MaxFileSize', { size: maxFileSizeMb })}</div>}
          </div>

          <input
            multiple={multiple}
            style={{ display: 'none' }}
            accept={fileTypes?.join(',')}
            ref={ref}
            onChange={handleFileUpload}
            onClick={clearInputValue}
            type={'file'}
            disabled={disabled}
          />
        </>
      )}

      <div className={Style.fileItemsContainer}>
        {value?.map((x) => (
          <FileItem key={x.fileName} attachment={x} onDelete={deleteFile} />
        ))}
      </div>
    </div>
  );
};

const FileItem: FC<{
  attachment: AppAttachmentValueWithErrors;
  onDelete: (file: AppAttachmentValue) => void;
}> = ({ attachment, onDelete }) => {
  const errors = useMemo(() => {
    if (!attachment.errors || (Array.isArray(attachment.errors) && !attachment.errors.length)) return null;
    if (attachment.errors && Array.isArray(attachment.errors) && attachment.errors.length)
      return attachment.errors.map((message) => <p key={message}>{message}</p>);
    return <p>{attachment.errors}</p>;
  }, [attachment.errors]);

  const fileSize = attachment.fileSizeBytes ?? attachment.file?.size;

  return (
    <div className={Style.fileItem}>
      <FileIcon />
      <div className={Style.nameWithInfo}>
        <div className={Style.nameWrapper}>
          <Tooltip hostStyles={Style.tooltipHost} text={attachment.fileName} styles={Style.tooltip}>
            <div className={Style.name}>{attachment.fileName}</div>
          </Tooltip>
        </div>
        {!isNil(fileSize) && <div className={Style.info}>{formatFileSize(fileSize)}</div>}
        {!!errors && <div className={Style.errors}>{errors}</div>}
      </div>
      <AppButton variant={'icon-link'} colorSchema={'basic'} Icon={CrossIcon} onClick={() => onDelete(attachment)} />
    </div>
  );
};

const fileToAppAttachmentValue = (file: File): AppAttachmentValue => ({
  id: null,
  fileName: file.name,
  file: file,
  fileSizeBytes: file.size,
});

const useDragOver = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  useEffect(() => {
    const setDraggedOver = () => setIsDraggedOver(true);
    const resetDraggedOver = () => setIsDraggedOver(false);

    ref.current?.addEventListener('dragover', setDraggedOver);
    ref.current?.addEventListener('dragleave', resetDraggedOver);
    ref.current?.addEventListener('dragend', resetDraggedOver);
    ref.current?.addEventListener('drop', resetDraggedOver);

    return () => {
      ref.current?.removeEventListener('dragover', setDraggedOver);
      ref.current?.removeEventListener('dragleave', resetDraggedOver);
      ref.current?.removeEventListener('dragend', resetDraggedOver);
      ref.current?.removeEventListener('drop', resetDraggedOver);
    };
  }, []);

  return { isDraggedOver, ref };
};
